import { api } from '@/lib/api/client';
import type {
  Review,
  ReviewFilters,
  ReviewsResponse,
  CreateReviewDto,
  UpdateReviewDto,
} from '../types';

const REVIEWS_URL = '/reviews';

export const reviewsApi = {
  // Get all reviews with filters
  getAll: async (filters?: ReviewFilters): Promise<ReviewsResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Handle search - only send if 2+ chars
    if (params['s'] && (params['s'] as string).length < 2) {
      delete params['s'];
    }

    // Handle date filters
    if (params['createdAt.min']) {
      const minDate = new Date(params['createdAt.min'] as string);
      minDate.setHours(0, 0, 0, 0);
      params['createdAt.min'] = minDate.toISOString();
    }

    if (params['createdAt.max']) {
      const maxDate = new Date(params['createdAt.max'] as string);
      maxDate.setHours(23, 59, 59, 999);
      params['createdAt.max'] = maxDate.toISOString();
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    // Remove order params if empty
    if (!params['orderBy'] || !params['order']) {
      delete params['orderBy'];
      delete params['order'];
    }

    return api.get<ReviewsResponse>(REVIEWS_URL, { params });
  },

  // Get single review by ID
  getById: async (id: number | string): Promise<Review> => {
    return api.get<Review>(`${REVIEWS_URL}/${id}`);
  },

  // Create review
  create: async (data: CreateReviewDto): Promise<Review> => {
    return api.post<Review>(REVIEWS_URL, data);
  },

  // Update review
  update: async ({ id, data }: { id: number | string; data: UpdateReviewDto }): Promise<Review> => {
    return api.patch<Review>(`${REVIEWS_URL}/${id}`, data);
  },

  // Delete review
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${REVIEWS_URL}/${id}`);
  },
};

// Query keys for React Query
export const reviewsQueryKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewsQueryKeys.all, 'list'] as const,
  list: (filters?: ReviewFilters) => [...reviewsQueryKeys.lists(), filters] as const,
  details: () => [...reviewsQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...reviewsQueryKeys.details(), id] as const,
};
